import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import { QuotationFlightInfo } from '../../types';
import { dayNumber, formatLayoverDuration, layoverBetweenSegments, monthNumber } from 'features/quotation/helpers';

interface FlightsTableProps {
	flightInfo: QuotationFlightInfo[];
}

const formatDate = (date: string) => `${dayNumber(date)} ${monthNumber(date)}`;
const formatHour = (hour: string) => hour.substring(0, 5);

export const FlightsTableDesktop = ({ flightInfo }: FlightsTableProps): JSX.Element => {
	return (
		<Box display="grid" gap={1.25} sx={{ p: 1.5, backgroundColor: 'rgba(49, 25, 75, 0.025)' }}>
			{flightInfo.map((info, index) => {
				const isLast = index === flightInfo.length - 1;
				return (
					<Box key={`${info.flightNumber}-${index}`}>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: 'minmax(160px, 220px) 1fr',
								gap: 2,
								p: 1.5,
								borderRadius: '12px',
								backgroundColor: 'white',
								border: '1px solid rgba(49, 25, 75, 0.10)',
								boxShadow: '0 6px 18px rgba(49, 25, 75, 0.06)',
							}}
						>
							<Box display="flex" alignItems="center" gap={1.25}>
								<Box
									sx={{
										width: 42,
										height: 42,
										borderRadius: '12px',
										display: 'grid',
										placeItems: 'center',
										backgroundColor: 'rgba(237, 102, 2, 0.08)',
									}}
								>
									<img src={info.airline.image} alt={info.airline.name} style={{ maxWidth: 30, maxHeight: 24 }} />
								</Box>
								<Box minWidth={0}>
									<Typography variant="body2" color="primary" fontWeight={900} noWrap>
										{info.airline.name}
									</Typography>
									<Typography variant="caption" color="text.secondary" fontWeight={800}>
										{info.flightNumber}
									</Typography>
								</Box>
							</Box>

							<Box display="grid" gridTemplateColumns="1fr auto 1fr" gap={2} alignItems="center">
								<Box minWidth={0}>
									<Typography variant="h6" color="primary" fontWeight={900} lineHeight={1}>
										{info.departure.airport.code}
									</Typography>
									<Typography variant="body2" color="primary" fontWeight={800} noWrap>
										{info.departure.airport.cityName}
									</Typography>
									<Typography variant="caption" color="text.secondary" display="block" noWrap>
										{info.departure.airport.name}
									</Typography>
									<Typography variant="body2" fontWeight={900} mt={0.75}>
										{formatHour(info.departure.hour)} · {formatDate(info.departure.date)}
									</Typography>
								</Box>

								<Box minWidth={150} textAlign="center">
									<FlightTakeoffIcon sx={{ color: 'secondary.main', fontSize: 20, mb: 0.5 }} />
									<Box
										sx={{
											position: 'relative',
											height: 3,
											borderRadius: '999px',
											background: 'linear-gradient(90deg, #31194B 0%, #ED6602 100%)',
											'&:before, &:after': {
												content: '""',
												position: 'absolute',
												top: -4,
												width: 11,
												height: 11,
												borderRadius: '50%',
												backgroundColor: '#31194B',
											},
											'&:before': { left: 0 },
											'&:after': { right: 0, backgroundColor: '#ED6602' },
										}}
									/>
								</Box>

								<Box minWidth={0} textAlign="right">
									<Typography variant="h6" color="primary" fontWeight={900} lineHeight={1}>
										{info.arrival.airport.code}
									</Typography>
									<Typography variant="body2" color="primary" fontWeight={800} noWrap>
										{info.arrival.airport.cityName}
									</Typography>
									<Typography variant="caption" color="text.secondary" display="block" noWrap>
										{info.arrival.airport.name}
									</Typography>
									<Typography variant="body2" fontWeight={900} mt={0.75}>
										{formatHour(info.arrival.hour)} · {formatDate(info.arrival.date)}
									</Typography>
								</Box>
							</Box>
						</Box>
						{!isLast &&
							(() => {
								// Sólo es conexión si es el mismo aeropuerto y la espera es <24h;
								// entre ida y vuelta (días después) no va chip.
								const layover = layoverBetweenSegments(info, flightInfo[index + 1]);
								if (!layover) return null;
								return (
									<Box display="flex" justifyContent="center" my={0.75}>
										<Chip
											size="small"
											label={`Conexión en ${layover.airportCode} · espera de ${formatLayoverDuration(layover.minutes)}`}
											sx={{
												borderRadius: '999px',
												fontWeight: 800,
												color: 'primary.main',
												backgroundColor: 'rgba(49, 25, 75, 0.06)',
											}}
										/>
									</Box>
								);
							})()}
					</Box>
				);
			})}
		</Box>
	);
};
